import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { type ColumnDef } from "@tanstack/react-table";
import {
  Search,
  LayoutGrid,
  Table as TableIcon,
  Refresh,
  Zap,
  Clock,
  Activity,
} from "@/components/icons";

import { fetchActiveSessions, ActiveSession } from "@/lib/api";
import { useDashboardOrg } from "@/app/DashboardOrgContext";
import { Stat } from "@/components/Stat";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { SlidePanel } from "@/components/SlidePanel";
import { DetailOverlay } from "@/components/ui/detail-overlay";
import { SessionDetailPanel } from "@/views/SessionDetailPanel";
import { DataTable } from "@/components/ui/data-table";
import { Card, CardContent } from "@/components/ui/card";
import { DataTableShell } from "@/components/ui/data-table-shell";

const PAGE_SIZE = 50;

function formatDuration(seconds: number): string {
  if (!seconds || seconds < 0) return "0m";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function formatEnergy(wh: number): string {
  if (!wh) return "0.00";
  return (wh / 1000).toFixed(2);
}

type ViewMode = "tiles" | "table";

const sessionColumns: ColumnDef<ActiveSession, any>[] = [
  {
    id: "unit",
    header: "Unit",
    cell: ({ row }) => {
      const raw = row.original.unit_name || "";
      const hasCustomName = raw && raw !== `Unit ${row.original.unit_id}`;
      return (
        <div className="overflow-hidden">
          {hasCustomName && <div className="font-semibold truncate">{raw}</div>}
          <div className="font-mono text-xs overflow-hidden text-ellipsis text-left opacity-60" dir="rtl">{row.original.unit_id}</div>
        </div>
      );
    },
  },
  {
    accessorKey: "energy_delivered",
    header: "Energy",
    cell: ({ getValue }) => <span className="font-semibold">{formatEnergy(getValue())} kWh</span>,
  },
  {
    accessorKey: "duration_seconds",
    header: "Duration",
    cell: ({ getValue }) => formatDuration(getValue()),
  },
  {
    accessorKey: "start_time",
    header: "Started",
    cell: ({ getValue }) => (
      <span className="text-xs">{getValue() ? new Date(getValue()).toLocaleString() : "—"}</span>
    ),
  },
  {
    accessorKey: "subsessions_count",
    header: "Subsessions",
    cell: ({ getValue }) => getValue() ?? 0,
  },
];

export function SessionsPage() {
  // Manager → org-scoped active sessions; admin → fleet. Declared early so the
  // pagination-reset effect can depend on it.
  const { dashboardOrgId, scopeReady } = useDashboardOrg();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortBy, setSortBy] = useState("start_time");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [offset, setOffset] = useState(0);
  const [allSessions, setAllSessions] = useState<ActiveSession[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>(
    () => (localStorage.getItem("sessionsViewMode") as ViewMode) || "table"
  );

  // Detail panel
  const [selectedSession, setSelectedSession] = useState<ActiveSession | null>(() => {
    const saved = localStorage.getItem("sessionsSelectedPanel");
    return saved ? JSON.parse(saved) : null;
  });
  const handleSessionClick = useCallback((s: ActiveSession) => {
    setSelectedSession(s);
    localStorage.setItem("sessionsSelectedPanel", JSON.stringify(s));
  }, []);
  const closePanel = useCallback(() => {
    setSelectedSession(null);
    localStorage.removeItem("sessionsSelectedPanel");
  }, []);

  // Sessions have no dedicated route, so double-click opens the same detail in
  // a full-area overlay (single click still opens the side panel).
  const [overlaySession, setOverlaySession] = useState<ActiveSession | null>(null);
  const handleSessionOpen = useCallback((s: ActiveSession) => setOverlaySession(s), []);

  // Debounce search — only updates debouncedSearch, no side effects
  const searchTimer = useRef<number>(0);
  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = window.setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(searchTimer.current);
  }, [search]);

  // Reset accumulated sessions when filters actually change (skip initial mount)
  const filtersInitialized = useRef(false);
  useEffect(() => {
    if (!filtersInitialized.current) {
      filtersInitialized.current = true;
      return;
    }
    setOffset(0);
    setAllSessions([]);
  }, [debouncedSearch, sortBy, sortOrder, dashboardOrgId]);

  const q = useQuery({
    queryKey: ["sessions", "active", dashboardOrgId, { offset, limit: PAGE_SIZE, sortBy, sortOrder, search: debouncedSearch }],
    queryFn: () =>
      fetchActiveSessions({
        limit: PAGE_SIZE,
        offset,
        sort_by: sortBy,
        sort_order: sortOrder,
        search: debouncedSearch || undefined,
        orgId: dashboardOrgId,
      }),
    enabled: scopeReady,
    refetchInterval: 10_000,
    placeholderData: keepPreviousData,
  });

  // Accumulate sessions for "load more"
  useEffect(() => {
    if (q.data?.active_sessions) {
      if (offset === 0) {
        setAllSessions(q.data.active_sessions);
      } else {
        setAllSessions((prev) => {
          const ids = new Set(prev.map((s) => `${s.unit_id}-${s.start_time}`));
          const newOnes = q.data.active_sessions.filter((s) => !ids.has(`${s.unit_id}-${s.start_time}`));
          return [...prev, ...newOnes];
        });
      }
    }
  }, [q.data, offset]);

  const totalCount = q.data?.total_count ?? 0;
  const totalEnergy = allSessions.reduce((sum, s) => sum + (s.energy_delivered || 0), 0);

  return (
    <div className="space-y-5 pt-[86px]">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Active Sessions</h1>
          <div className="text-sm opacity-70">Live charging sessions across all units</div>
        </div>
        <Button variant="ghost" size="sm" onClick={() => q.refetch()}>
          <Refresh className="h-4 w-4" /> Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid gap-3 md:grid-cols-3">
        <Stat
          title="Active Sessions"
          value={totalCount}
          description="currently charging"
          icon={<Activity className="h-4 w-4 opacity-60" />}
        />
        <Stat
          title="Total Energy"
          value={`${formatEnergy(totalEnergy)} kWh`}
          description="across all active sessions"
          icon={<Zap className="h-4 w-4 opacity-60" />}
        />
        <Stat
          title="Auto-refresh"
          value="10s"
          description="data refreshes automatically"
          icon={<Clock className="h-4 w-4 opacity-60" />}
        />
      </div>

      {/* Controls */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex flex-1 min-w-[240px]">
              <Button variant="ghost" size="sm"><Search className="h-4 w-4" /></Button>
              <input
                className="h-8 w-full rounded-4xl border border-input bg-input/30 px-3 text-sm outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
                placeholder="Search by unit name or ID..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger size="sm">
                <SelectValue placeholder="Sort by Start Time" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="start_time">Sort by Start Time</SelectItem>
                <SelectItem value="energy">Sort by Energy</SelectItem>
                <SelectItem value="duration">Sort by Duration</SelectItem>
                <SelectItem value="unit_id">Sort by Unit ID</SelectItem>
              </SelectContent>
            </Select>

            <Select value={sortOrder} onValueChange={(v) => setSortOrder(v as "asc" | "desc")}>
              <SelectTrigger size="sm">
                <SelectValue placeholder="Descending" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="desc">Descending</SelectItem>
                <SelectItem value="asc">Ascending</SelectItem>
              </SelectContent>
            </Select>

            <ToggleGroup type="single" size="sm" variant="outline" value={viewMode} onValueChange={(v) => { if (v) { setViewMode(v as ViewMode); localStorage.setItem("sessionsViewMode", v); } }}>
              <ToggleGroupItem value="tiles" aria-label="Tile View">
                <LayoutGrid className="h-4 w-4" />
              </ToggleGroupItem>
              <ToggleGroupItem value="table" aria-label="Table View">
                <TableIcon className="h-4 w-4" />
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
        </CardContent>
      </Card>

      {/* Content + Detail Panel */}
      <div className="flex gap-4 items-stretch">
        <div className="flex-1 min-w-0">
          {q.data?.no_units_assigned ? (
            <Card>
              <CardContent className="items-center text-center">
                <div className="text-lg font-semibold">No Units Assigned</div>
                <div className="opacity-70">Contact your administrator to get access.</div>
              </CardContent>
            </Card>
          ) : viewMode === "tiles" ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {allSessions.map((s, idx) => (
                <SessionCard
                  key={`${s.unit_id}-${idx}`}
                  session={s}
                  selected={selectedSession?.unit_id === s.unit_id && selectedSession?.start_time === s.start_time}
                  onClick={() => handleSessionClick(s)}
                  onDoubleClick={() => handleSessionOpen(s)}
                />
              ))}
              {allSessions.length === 0 && !q.isLoading && (
                <div className="col-span-full text-center py-10 opacity-60">No active sessions</div>
              )}
            </div>
          ) : (
            <DataTableShell>
              <DataTable
                columns={sessionColumns}
                data={allSessions}
                isLoading={q.isLoading && allSessions.length === 0}
                getRowId={(s) => `${s.unit_id}-${s.start_time}`}
                onRowClick={handleSessionClick}
                onRowDoubleClick={handleSessionOpen}
                selectedRowId={selectedSession ? `${selectedSession.unit_id}-${selectedSession.start_time}` : undefined}
                emptyMessage="No active sessions"
              />
            </DataTableShell>
          )}

          {/* Load More */}
          {allSessions.length < totalCount && (
            <div className="flex justify-center mt-4">
              <Button
                variant="outline"
                disabled={q.isFetching}
                onClick={() => setOffset((prev) => prev + PAGE_SIZE)}
              >
                {q.isFetching ? "Loading..." : "Load More"}
              </Button>
            </div>
          )}

          <div className="text-center text-sm opacity-60 mt-2">
            Showing {allSessions.length} of {totalCount} sessions
          </div>
        </div>

        {/* Detail panel */}
        <SlidePanel
          open={!!selectedSession}
          onClose={closePanel}
          title="Session Details"
        >
          {selectedSession && <SessionDetailPanel session={selectedSession} />}
        </SlidePanel>

        {/* Full-area detail (double-click) */}
        <DetailOverlay
          open={!!overlaySession}
          onClose={() => setOverlaySession(null)}
          title="Session Details"
        >
          {overlaySession && <SessionDetailPanel session={overlaySession} />}
        </DetailOverlay>
      </div>
    </div>
  );
}

function SessionCard({ session: s, selected, onClick, onDoubleClick }: { session: ActiveSession; selected: boolean; onClick: () => void; onDoubleClick?: () => void }) {
  return (
    <Card
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      className={[
        "transition cursor-pointer select-none",
        selected ? "ring-2 ring-primary" : "",
      ].join(" ")}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            {s.unit_name && s.unit_name !== `Unit ${s.unit_id}` && (
              <div className="font-semibold">{s.unit_name}</div>
            )}
            <div className="font-mono text-xs overflow-hidden text-ellipsis text-left opacity-60" dir="rtl">{s.unit_id}</div>
          </div>
          <Badge variant="success">Charging</Badge>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
          <div className="opacity-70">Energy</div>
          <div className="text-right font-semibold">{formatEnergy(s.energy_delivered)} kWh</div>
          <div className="opacity-70">Duration</div>
          <div className="text-right font-semibold">{formatDuration(s.duration_seconds)}</div>
          <div className="opacity-70">Subsessions</div>
          <div className="text-right">{s.subsessions_count ?? 0}</div>
        </div>
        {s.start_time && (
          <div className="mt-2 text-xs opacity-60">
            Started: {new Date(s.start_time).toLocaleString()}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
